import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// Utils
import { isTouchDevice } from '@sketch/utils'

// Constants
import { SKETCH_WEBSITE } from '@sketch/constants'
import { useFlag } from 'containers/flags'

// Hooks
import {
  useResponsiveDropdown,
  BaseResponsiveDropdownProps,
} from 'components/ResponsiveDropdown'

// Components
import {
  BaseButtonItem,
  ButtonItem,
  ExternalLinkItem,
} from 'modules/projects/components/Item'

// Local Icons
import { icons } from '../../icons'

// Styles
import { Chevron } from '../BottomMenu/BottomMenu.styles'
import {
  DropdownDivider,
  DropdownWrapper,
  DropdownItem,
  DropdownHeader,
} from './HelpMenu.styles'

// Config
import { useHubspotChat } from 'utils/hooks/useHubspotChat'

interface HelpMenuProps {
  title?: string
}

interface HelpMenuContentProps extends BaseResponsiveDropdownProps {
  hasHubspotChatSupportOn: boolean
  loadChat: () => void
}

/**
 *
 * HelpMenuContent
 *
 * Renders the content for the new Sidebar Help menu
 * found at the bottom of the sidebar
 */
const HelpMenuContent: React.FC<HelpMenuContentProps> = ({
  hasHubspotChatSupportOn,
  loadChat,
  currentVariant,
}) => {
  const isModalVariant = currentVariant === 'modal'
  const isTouch = isTouchDevice()

  return (
    <DropdownWrapper isModalVariant={isModalVariant}>
      {isModalVariant && <DropdownHeader>Help</DropdownHeader>}
      <DropdownItem>
        <ExternalLinkItem
          icon={icons.Home}
          href={`${SKETCH_WEBSITE}/home`}
          text="Visit Sketch.com"
        />
      </DropdownItem>
      <DropdownItem>
        <ExternalLinkItem
          icon={icons.Desktop}
          href={`${SKETCH_WEBSITE}/downloads/mac/`}
          text="Get Sketch for Mac"
        />
      </DropdownItem>
      <DropdownItem>
        <ExternalLinkItem
          icon={icons.Phone}
          href="https://apps.apple.com/app/sketch-view-and-mirror/id1609224699"
          text="Get Sketch for iOS"
        />
      </DropdownItem>
      <DropdownDivider />

      <DropdownItem>
        <ExternalLinkItem
          icon={icons.Notebook}
          href={`${SKETCH_WEBSITE}/docs/`}
          text="Documentation"
        />
      </DropdownItem>
      <DropdownItem>
        <ExternalLinkItem
          icon={icons.QuestionMark}
          href={`${SKETCH_WEBSITE}/support/general/`}
          text="FAQs"
        />
      </DropdownItem>
      {!isTouch && (
        <DropdownItem>
          <ExternalLinkItem
            icon={icons.Command}
            href={`${SKETCH_WEBSITE}/docs/designing/shortcuts/#web-app-shortcuts`}
            text="Keyboard Shortcuts"
          />
        </DropdownItem>
      )}
      {hasHubspotChatSupportOn && (
        <ButtonItem
          icon={icons.ChatIcon}
          onClick={loadChat}
          text="Chat with Us"
        />
      )}
      <DropdownItem>
        <ExternalLinkItem
          icon={icons.MailEnvelope}
          href={`${SKETCH_WEBSITE}/support/contact/`}
          text="Contact Us"
        />
      </DropdownItem>
    </DropdownWrapper>
  )
}

/**
 *
 * HelpMenu
 *
 * Renders the HelpMenu used in the "BottomMenu" on the "Sidebar"
 */
const HelpMenu: React.FC<HelpMenuProps> = ({ title = 'Help with Sketch' }) => {
  const hasHubspotChatSupportOn = useFlag('hubspot-chat-support')
  const { loadChat } = useHubspotChat()

  const { search } = useLocation()

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: HelpMenuContent,
    placement: 'right-end',
    dropdownProps: {
      hasHubspotChatSupportOn,
      loadChat,
    },
  })

  // only try to load the chat on the first load (avoids eternal loops)
  useEffect(() => {
    // check if chatWithUs is on the URl to auto load hubspot chat
    const URLparams = new URLSearchParams(search)
    const isChatWithUsLaunch = URLparams.get('chatWithUs')

    if (isChatWithUsLaunch === 'true') {
      loadChat()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      <BaseButtonItem
        icon={icons.LifeBelt}
        text={title}
        additional={<Chevron />}
        {...buttonProps}
      />
      {content}
    </>
  )
}

export default HelpMenu
